// badgeEngine.js — Badge milestone definitions and checking logic// Badge engine v2

import { supabase } from '../supabaseClient';

// ─────────────────────────────────────────────
// BADGE DEFINITIONS
// ─────────────────────────────────────────────

// Mandino original 10 scroll identifiers in sequence
const MANDINO_SCROLLS = [
  'habit', 'love', 'persistence', 'selfEsteem', 'presence',
  'emotionalMastery', 'joy', 'growth', 'action', 'wisdom',
];

export const BADGE_DEFINITIONS = {

  // ── Streak badges (10-point star shape) ──
  streak_7: {
    id: 'streak_7',
    name: 'The Faithful Student',
    description: 'Maintained a 7-day reading streak',
    category: 'streak',
    shape: 'star10',
    color: '#14b8a6',
    icon: '🔥',
    milestone: 7,
  },
  streak_21: {
    id: 'streak_21',
    name: 'The Devoted',
    description: 'Maintained a 21-day reading streak',
    category: 'streak',
    shape: 'star10',
    color: '#0ea5e9',
    icon: '🔥',
    milestone: 21,
  },
  streak_30: {
    id: 'streak_30',
    name: 'The Unstoppable',
    description: 'Maintained a 30-day reading streak',
    category: 'streak',
    shape: 'star10',
    color: '#f59e0b',
    icon: '🔥',
    milestone: 30,
  },
  streak_90: {
    id: 'streak_90',
    name: 'The Legendary',
    description: 'Maintained a 90-day reading streak',
    category: 'streak',
    shape: 'star10',
    color: '#f97316',
    icon: '🔥',
    milestone: 90,
  },
  streak_365: {
    id: 'streak_365',
    name: 'The Immortal',
    description: 'Maintained a 365-day reading streak',
    category: 'streak',
    shape: 'star10',
    color: '#a855f7',
    icon: '🔥',
    milestone: 365,
  },

  // ── Perfect day badges (circle shape) ──
  perfect_1: {
    id: 'perfect_1',
    name: 'First Light',
    description: 'Completed your first perfect day — all three readings',
    category: 'perfect',
    shape: 'circle',
    color: '#14b8a6',
    icon: '⭐',
    milestone: 1,
  },
  perfect_7: {
    id: 'perfect_7',
    name: 'The Disciplined',
    description: 'Completed 7 perfect days',
    category: 'perfect',
    shape: 'circle',
    color: '#0ea5e9',
    icon: '⭐',
    milestone: 7,
  },
  perfect_30: {
    id: 'perfect_30',
    name: 'The Immaculate',
    description: 'Completed 30 perfect days',
    category: 'perfect',
    shape: 'circle',
    color: '#f59e0b',
    icon: '⭐',
    milestone: 30,
  },
  perfect_100: {
    id: 'perfect_100',
    name: 'The Transcendent',
    description: 'Completed 100 perfect days',
    category: 'perfect',
    shape: 'circle',
    color: '#a855f7',
    icon: '⭐',
    milestone: 100,
  },

  // ── Total days badges (rounded square shape) ──
  days_7: {
    id: 'days_7',
    name: 'The Initiate',
    description: 'Completed 7 total qualifying days',
    category: 'days',
    shape: 'square',
    color: '#14b8a6',
    icon: '📅',
    milestone: 7,
  },
  days_100: {
    id: 'days_100',
    name: 'The Seeker',
    description: 'Completed 100 total qualifying days',
    category: 'days',
    shape: 'square',
    color: '#0ea5e9',
    icon: '📅',
    milestone: 100,
  },
  days_200: {
    id: 'days_200',
    name: 'The Scholar',
    description: 'Completed 200 total qualifying days',
    category: 'days',
    shape: 'square',
    color: '#f59e0b',
    icon: '📅',
    milestone: 200,
  },
  days_365: {
    id: 'days_365',
    name: 'The Master',
    description: 'Completed 365 total qualifying days',
    category: 'days',
    shape: 'square',
    color: '#f97316',
    icon: '📅',
    milestone: 365,
  },
  days_500: {
    id: 'days_500',
    name: 'The Sage',
    description: 'Completed 500 total qualifying days',
    category: 'days',
    shape: 'square',
    color: '#a855f7',
    icon: '📅',
    milestone: 500,
  },

  // ── Scroll completion badges (shield shape) ──
  scroll_habit: {
    id: 'scroll_habit',
    name: 'Keeper of the Sacred Oath',
    description: 'Completed 30 days with the Scroll of Habit',
    category: 'scroll',
    shape: 'shield',
    color: '#14b8a6',
    icon: '✓',
    scrollId: 'habit',
  },
  scroll_love: {
    id: 'scroll_love',
    name: 'Herald of the Open Heart',
    description: 'Completed 30 days with the Scroll of Love',
    category: 'scroll',
    shape: 'shield',
    color: '#ec4899',
    icon: '♡',
    scrollId: 'love',
  },
  scroll_persistence: {
    id: 'scroll_persistence',
    name: 'The Unyielding',
    description: 'Completed 30 days with the Scroll of Persistence',
    category: 'scroll',
    shape: 'shield',
    color: '#f59e0b',
    icon: '⚓',
    scrollId: 'persistence',
  },
  scroll_selfEsteem: {
    id: 'scroll_selfEsteem',
    name: 'Pillar of Self-Belief',
    description: 'Completed 30 days with the Scroll of Self-Esteem',
    category: 'scroll',
    shape: 'shield',
    color: '#6366f1',
    icon: '◈',
    scrollId: 'selfEsteem',
  },
  scroll_presence: {
    id: 'scroll_presence',
    name: 'The Present One',
    description: 'Completed 30 days with the Scroll of Presence',
    category: 'scroll',
    shape: 'shield',
    color: '#22c55e',
    icon: '◉',
    scrollId: 'presence',
  },
  scroll_emotionalMastery: {
    id: 'scroll_emotionalMastery',
    name: 'Master of the Inner Storm',
    description: 'Completed 30 days with the Scroll of Emotional Mastery',
    category: 'scroll',
    shape: 'shield',
    color: '#0ea5e9',
    icon: '⚡',
    scrollId: 'emotionalMastery',
  },
  scroll_joy: {
    id: 'scroll_joy',
    name: 'The Radiant',
    description: 'Completed 30 days with the Scroll of Joy',
    category: 'scroll',
    shape: 'shield',
    color: '#eab308',
    icon: '☀',
    scrollId: 'joy',
  },
  scroll_growth: {
    id: 'scroll_growth',
    name: 'The Ever-Becoming',
    description: 'Completed 30 days with the Scroll of Growth',
    category: 'scroll',
    shape: 'shield',
    color: '#10b981',
    icon: '↑',
    scrollId: 'growth',
  },
  scroll_action: {
    id: 'scroll_action',
    name: 'The Mover of Worlds',
    description: 'Completed 30 days with the Scroll of Action',
    category: 'scroll',
    shape: 'shield',
    color: '#f97316',
    icon: '▶',
    scrollId: 'action',
  },
  scroll_wisdom: {
    id: 'scroll_wisdom',
    name: 'The Still Voice',
    description: 'Completed 30 days with the Scroll of Wisdom',
    category: 'scroll',
    shape: 'shield',
    color: '#8b5cf6',
    icon: '◎',
    scrollId: 'wisdom',
  },
  scroll_confidence: {
    id: 'scroll_confidence',
    name: 'The Unshakeable',
    description: 'Completed 30 days with the Scroll of Confidence',
    category: 'scroll',
    shape: 'shield',
    color: '#14b8a6',
    icon: '◆',
    scrollId: 'confidence',
  },
  scroll_discipline: {
    id: 'scroll_discipline',
    name: 'The Iron Will',
    description: 'Completed 30 days with the Scroll of Discipline',
    category: 'scroll',
    shape: 'shield',
    color: '#6366f1',
    icon: '⬡',
    scrollId: 'discipline',
  },
  scroll_authenticity: {
    id: 'scroll_authenticity',
    name: 'The True Self',
    description: 'Completed 30 days with the Scroll of Authenticity',
    category: 'scroll',
    shape: 'shield',
    color: '#ec4899',
    icon: '✦',
    scrollId: 'authenticity',
  },
  scroll_integrity: {
    id: 'scroll_integrity',
    name: 'The Covenant Keeper',
    description: 'Completed 30 days with the Scroll of Integrity',
    category: 'scroll',
    shape: 'shield',
    color: '#f59e0b',
    icon: '⬟',
    scrollId: 'integrity',
  },
    scroll_gratitude: {
    id: 'scroll_gratitude',
    name: 'The Grateful Heart',
    description: 'Completed 30 days with the Scroll of Gratitude',
    category: 'scroll',
    shape: 'shield',
    color: '#10b981',
    icon: '✿',
    scrollId: 'gratitude',
  },
  scroll_courage: {
    id: 'scroll_courage',
    name: 'The Fearless',
    description: 'Completed 30 days with the Scroll of Courage',
    category: 'scroll',
    shape: 'shield',
    color: '#f97316',
    icon: '⚔',
    scrollId: 'courage',
  },
  scroll_wealth: {
    id: 'scroll_wealth',
    name: 'The Abundant',
    description: 'Completed 30 days with the Scroll of Wealth',
    category: 'scroll',
    shape: 'shield',
    color: '#eab308',
    icon: '◇',
    scrollId: 'wealth',
  },
  scroll_charisma: {
    id: 'scroll_charisma',
    name: 'The Magnetic',
    description: 'Completed 30 days with the Scroll of Charisma',
    category: 'scroll',
    shape: 'shield',
    color: '#ec4899',
    icon: '✺',
    scrollId: 'charisma',
  },
  scroll_heartbreakRecovery: {
    id: 'scroll_heartbreakRecovery',
    name: 'The Rising Heart',
    description: 'Completed 30 days with the Heartbreak Recovery Scroll',
    category: 'scroll',
    shape: 'shield',
    color: '#f43f5e',
    icon: '♡',
    scrollId: 'heartbreakRecovery',
  },
  scroll_understanding: {
    id: 'scroll_understanding',
    name: 'The Deep Listener',
    description: 'Completed 30 days with the Scroll of Understanding',
    category: 'scroll',
    shape: 'shield',
    color: '#0ea5e9',
    icon: '◉',
    scrollId: 'understanding',
  },
  scroll_curiosity: {
    id: 'scroll_curiosity',
    name: 'The Explorer',
    description: 'Completed 30 days with the Scroll of Curiosity',
    category: 'scroll',
    shape: 'shield',
    color: '#8b5cf6',
    icon: '◈',
    scrollId: 'curiosity',
  },
  scroll_selfApproval: {
    id: 'scroll_selfApproval',
    name: 'The Self-Sovereign',
    description: 'Completed 30 days with the Scroll of Self-Approval',
    category: 'scroll',
    shape: 'shield',
    color: '#14b8a6',
    icon: '✦',
    scrollId: 'selfApproval',
  },

  // ── Journey badges (hexagon shape) ──
  journey_original_ten: {
    id: 'journey_original_ten',
    name: 'The Original Ten',
    description: 'Completed all 10 scrolls from Og Mandino\'s original sequence',
    category: 'journey',
    shape: 'hexagon',
    color: '#f59e0b',
    icon: '✦',
  },
  journey_greatest_self: {
    id: 'journey_greatest_self',
    name: 'My Greatest Self',
    description: 'Completed 25 scrolls — you have become your greatest self',
    category: 'journey',
    shape: 'hexagon',
    color: '#a855f7',
    icon: '★',
  },
};

// ─────────────────────────────────────────────
// SESSION GUARD
// Tracks which badges have already triggered a
// popup this session to prevent double-firing.
// ─────────────────────────────────────────────
const sessionAwardedBadges = new Set();

// ─────────────────────────────────────────────
// CORE CHECK FUNCTION
// Called after every trackScrollReading().
// Returns array of newly earned badge definitions
// (empty array if nothing new earned this call).
// ─────────────────────────────────────────────
export const checkBadges = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    // Load all stats in parallel
    const [
      streakData,
      readingsData,
      progressData,
      existingBadgesData,
    ] = await Promise.all([
      getStreakStats(user.id),
      getReadingStats(user.id),
      getScrollProgressStats(user.id),
      getExistingBadges(user.id),
    ]);

    const existingBadgeIds = new Set(existingBadgesData);
    const newlyEarned = [];

    // ── Check streak badges ──
    for (const badgeId of ['streak_7', 'streak_21', 'streak_30', 'streak_90', 'streak_365']) {
      const def = BADGE_DEFINITIONS[badgeId];
      if (!existingBadgeIds.has(badgeId) && streakData.currentStreak >= def.milestone) {
        await awardBadge(user.id, badgeId);
        if (!sessionAwardedBadges.has(badgeId)) {
          sessionAwardedBadges.add(badgeId);
          newlyEarned.push(def);
        }
      }
    }

    // ── Check perfect day badges ──
    for (const badgeId of ['perfect_1', 'perfect_7', 'perfect_30', 'perfect_100']) {
      const def = BADGE_DEFINITIONS[badgeId];
      if (!existingBadgeIds.has(badgeId) && readingsData.perfectDays >= def.milestone) {
        await awardBadge(user.id, badgeId);
        if (!sessionAwardedBadges.has(badgeId)) {
          sessionAwardedBadges.add(badgeId);
          newlyEarned.push(def);
        }
      }
    }

    // ── Check total days badges ──
    for (const badgeId of ['days_7', 'days_100', 'days_200', 'days_365', 'days_500']) {
      const def = BADGE_DEFINITIONS[badgeId];
      if (!existingBadgeIds.has(badgeId) && readingsData.totalQualifyingDays >= def.milestone) {
        await awardBadge(user.id, badgeId);
        if (!sessionAwardedBadges.has(badgeId)) {
          sessionAwardedBadges.add(badgeId);
          newlyEarned.push(def);
        }
      }
    }

    // ── Check scroll completion badges ──
    for (const [badgeId, def] of Object.entries(BADGE_DEFINITIONS)) {
      if (def.category !== 'scroll') continue;
      if (existingBadgeIds.has(badgeId)) continue;
      const progress = progressData[def.scrollId];
      if (progress?.isCompleted) {
        await awardBadge(user.id, badgeId);
        if (!sessionAwardedBadges.has(badgeId)) {
          sessionAwardedBadges.add(badgeId);
          newlyEarned.push(def);
        }
      }
    }

    // ── Check journey badges ──
    // Original Ten: all 10 Mandino scrolls complete
    if (!existingBadgeIds.has('journey_original_ten')) {
      const allTenComplete = MANDINO_SCROLLS.every(
        id => progressData[id]?.isCompleted
      );
      if (allTenComplete) {
        await awardBadge(user.id, 'journey_original_ten');
        const def = BADGE_DEFINITIONS['journey_original_ten'];
        if (!sessionAwardedBadges.has('journey_original_ten')) {
          sessionAwardedBadges.add('journey_original_ten');
          newlyEarned.push(def);
        }
      }
    }

    // My Greatest Self: 25 scrolls complete
    if (!existingBadgeIds.has('journey_greatest_self')) {
      const completedCount = Object.values(progressData).filter(p => p.isCompleted).length;
      if (completedCount >= 25) {
        await awardBadge(user.id, 'journey_greatest_self');
        const def = BADGE_DEFINITIONS['journey_greatest_self'];
        if (!sessionAwardedBadges.has('journey_greatest_self')) {
          sessionAwardedBadges.add('journey_greatest_self');
          newlyEarned.push(def);
        }
      }
    }

    return newlyEarned;
  } catch (error) {
    console.error('Error checking badges:', error);
    return [];
  }
};

// ─────────────────────────────────────────────
// GET ALL EARNED BADGES FOR A USER
// Used by the Progress page to display badge grid
// ─────────────────────────────────────────────
export const getUserBadges = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('user_badges')
      .select('badge_id, earned_at')
      .eq('user_id', user.id)
      .order('earned_at', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error getting user badges:', error);
    return [];
  }
};

// ─────────────────────────────────────────────
// INTERNAL HELPERS
// ─────────────────────────────────────────────

const awardBadge = async (userId, badgeId) => {
  try {
    const { error } = await supabase
      .from('user_badges')
      .insert({ user_id: userId, badge_id: badgeId })
      .select();
    // Ignore unique constraint errors — badge already exists
    if (error && error.code !== '23505') {
      console.error('Error awarding badge:', error);
    }
  } catch (error) {
    console.error('Error in awardBadge:', error);
  }
};

const getExistingBadges = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('user_badges')
      .select('badge_id')
      .eq('user_id', userId);
    if (error) throw error;
    return (data || []).map(row => row.badge_id);
  } catch (error) {
    console.error('Error getting existing badges:', error);
    return [];
  }
};

const getStreakStats = async (userId) => {
  try {
    const { data: readings, error } = await supabase
      .from('scroll_readings')
      .select('date, reading_number')
      .eq('user_id', userId)
      .order('date', { ascending: false });

    if (error) throw error;
    if (!readings || readings.length === 0) return { currentStreak: 0 };

    const dailyReadings = {};
    readings.forEach(r => {
      dailyReadings[r.date] = (dailyReadings[r.date] || 0) + 1;
    });

    const getLocalDateStr = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };

    const today = new Date();
    const todayStr = getLocalDateStr(today);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = getLocalDateStr(yesterday);

    const mostRecentDate = readings[0]?.date;
    if (!mostRecentDate) return { currentStreak: 0 };

    if (mostRecentDate !== todayStr && mostRecentDate !== yesterdayStr) {
      return { currentStreak: 0 };
    }

    let currentStreak = 0;
    let checkDate = new Date(mostRecentDate + 'T12:00:00');

    let active = true;
    while (active) {
      const dateStr = getLocalDateStr(checkDate);
      if (dailyReadings[dateStr] > 0) {
        currentStreak++;
        checkDate.setDate(checkDate.getDate() - 1);
      } else {
        active = false;
      }
    }

    return { currentStreak };
  } catch (error) {
    console.error('Error getting streak stats:', error);
    return { currentStreak: 0 };
  }
};

const getReadingStats = async (userId) => {
  try {
    const { data: readings, error } = await supabase
      .from('scroll_readings')
      .select('date, reading_number')
      .eq('user_id', userId);

    if (error) throw error;
    if (!readings || readings.length === 0) {
      return { totalQualifyingDays: 0, perfectDays: 0 };
    }

    // Group by date
    const byDate = {};
    readings.forEach(r => {
      if (!byDate[r.date]) byDate[r.date] = new Set();
      byDate[r.date].add(r.reading_number);
    });

    let totalQualifyingDays = 0;
    let perfectDays = 0;

    Object.values(byDate).forEach(readingSet => {
      if (readingSet.size >= 2) totalQualifyingDays++;
      if (readingSet.size >= 3) perfectDays++;
    });

    return { totalQualifyingDays, perfectDays };
  } catch (error) {
    console.error('Error getting reading stats:', error);
    return { totalQualifyingDays: 0, perfectDays: 0 };
  }
};

const getScrollProgressStats = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('scroll_progress')
      .select('scroll_id, qualifying_days, is_completed')
      .eq('user_id', userId);

    if (error) throw error;

    const progressMap = {};
    (data || []).forEach(row => {
      progressMap[row.scroll_id] = {
        qualifyingDays: row.qualifying_days,
        isCompleted: row.is_completed,
      };
    });

    return progressMap;
  } catch (error) {
    console.error('Error getting scroll progress stats:', error);
    return {};
  }
};

export default {
  checkBadges,
  getUserBadges,
  BADGE_DEFINITIONS,
};